// Emoji handling for the web client.
//
// Two jobs:
//
//  1. Grapheme-safe string handling. JS strings are UTF-16 code units, so
//     `charAt(0)`, `substr(0, n)` and `slice(0, n)` happily cut an emoji in
//     half and leave a lone surrogate behind, which every browser renders as
//     U+FFFD ("�"). That is what "emoji are not recognized" looks like in the
//     chat list, in avatar initials and in truncated titles.
//
//  2. The data + persistence behind the composer's emoji picker: a catalog
//     mirroring the iOS EmojiCatalog, name/alias search, and a recently-used
//     list persisted in localStorage so it survives a reload.

// ---------------------------------------------------------------- graphemes

const _segmenter = (typeof Intl != 'undefined' && typeof Intl.Segmenter == 'function') ?
  new Intl.Segmenter(undefined, {granularity: 'grapheme'}) : null;

// Split a string into user-perceived characters. Falls back to code points
// (which still never splits a surrogate pair) when Intl.Segmenter is missing.
export function graphemes(str) {
  if (typeof str != 'string' || !str) {
    return [];
  }
  if (_segmenter) {
    const out = [];
    for (const {segment} of _segmenter.segment(str)) {
      out.push(segment);
    }
    return out;
  }
  return Array.from(str);
}

// Number of user-perceived characters.
export function graphemeLength(str) {
  return graphemes(str).length;
}

// First user-perceived character, e.g. for an avatar initial. Unlike
// `str.charAt(0)` this returns a whole emoji (including flags, skin tones and
// ZWJ sequences) instead of half a surrogate pair.
export function firstGrapheme(str) {
  if (typeof str != 'string') {
    return str;
  }
  const g = graphemes(str);
  return g.length ? g[0] : '';
}

// Truncate to at most `maxLength` user-perceived characters. Never returns a
// lone surrogate. `maxLength` <= 0 yields an empty string.
export function truncateGraphemes(str, maxLength) {
  if (typeof str != 'string') {
    return str;
  }
  if (maxLength <= 0) {
    return '';
  }
  // Fast path: pure BMP strings need no segmentation.
  if (str.length <= maxLength && !/[\uD800-\uDBFF]/.test(str)) {
    return str;
  }
  const g = graphemes(str);
  return g.length <= maxLength ? str : g.slice(0, maxLength).join('');
}

// Last `count` user-perceived characters, grapheme-safe counterpart of
// `str.slice(-count)`.
export function lastGraphemes(str, count) {
  if (typeof str != 'string' || count <= 0) {
    return '';
  }
  const g = graphemes(str);
  return g.slice(Math.max(0, g.length - count)).join('');
}

// ---------------------------------------------------------------- emoji-only

// `\p{RGI_Emoji}` requires the ES2024 RegExp `v` flag. Constructing it throws a
// SyntaxError on engines without it (Safari < 17, Chrome < 112, Firefox < 116),
// and chat-message.jsx used to build it inline during render — one unsupported
// browser and the whole message list blows up. Build it once, defensively.
const _emojiOnlyRe = (() => {
  try {
    return new RegExp('^(?:\\p{RGI_Emoji}|\\uFE0F|\\u200D)+$', 'v');
  } catch (e) {
    return null;
  }
})();

const _emojiGlobalRe = (() => {
  try {
    return new RegExp('\\p{RGI_Emoji}', 'gv');
  } catch (e) {
    return null;
  }
})();

// If `text` is nothing but 1..max emoji, return how many. Otherwise 0.
// Returns 0 (rather than throwing) on engines without RegExp `v` support, so
// the message simply renders at normal size.
export function emojiOnlyCount(text, max = 5) {
  if (typeof text != 'string' || !text || !_emojiOnlyRe || !_emojiGlobalRe) {
    return 0;
  }
  const trimmed = text.trim();
  if (!trimmed || !_emojiOnlyRe.test(trimmed)) {
    return 0;
  }
  const m = trimmed.match(_emojiGlobalRe);
  const n = m ? m.length : 0;
  return n >= 1 && n <= max ? n : 0;
}

// ---------------------------------------------------------------- catalog

// Mirrors ios/Tinodios/EmojiPickerViewController.swift EmojiCatalog so both
// clients offer the same set.
export const EMOJI_CATEGORIES = [
  {id: 'smileys', icon: '😀', title: 'Smileys & Emotion', emojis: [
    '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩',
    '😘','😗','😚','😙','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨',
    '😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕',
    '🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','😎','🤓','🧐','😕','😟','🙁',
    '😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞',
    '😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','💩','🤡','👻','👽','🤖']},
  {id: 'people', icon: '👍', title: 'Gestures & People', emojis: [
    '👍','👎','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','👇','☝️','✋',
    '🤚','🖐️','🖖','👋','🤝','👏','🙌','👐','🤲','🙏','✍️','💅','🤳','💪','🦾','🦵',
    '🦶','👂','👃','🧠','🦷','👀','👁️','👅','👄','💋','🧑','👶','👦','👧','👨','👩',
    '🧓','👴','👵','🙍','🙎','🙅','🙆','💁','🙋','🧏','🤦','🤷','👮','🕵️','💂','👷']},
  {id: 'symbols', icon: '❤️', title: 'Hearts & Symbols', emojis: [
    '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖',
    '💘','💝','💟','☮️','✝️','☪️','🕉️','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈',
    '✨','⭐','🌟','💫','💥','🔥','🌈','☀️','🌤️','⛅','🌧️','⚡','❄️','💧','🌊','✅',
    '❌','❗','❓','‼️','⁉️','💯','🔔','🔕','💬','💭','🗯️','♻️','⚠️','🚫','💢','💤']},
  {id: 'animals', icon: '🐶', title: 'Animals & Nature', emojis: [
    '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🙈',
    '🙉','🙊','🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛',
    '🦋','🐌','🐞','🐜','🦗','🕷️','🦂','🐢','🐍','🦎','🦖','🐙','🦑','🦐','🦀','🐡',
    '🐠','🐟','🐬','🐳','🐋','🦈','🐊','🐅','🐆','🦓','🦍','🐘','🦏','🐪','🐫','🦒',
    '🌵','🎄','🌲','🌳','🌴','🌱','🌿','☘️','🍀','🎍','🌾','🌷','🌹','🥀','🌺','🌸','🌼','🌻']},
  {id: 'food', icon: '🍕', title: 'Food & Drink', emojis: [
    '🍏','🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥',
    '🥝','🍅','🍆','🥑','🥦','🥬','🥒','🌶️','🌽','🥕','🧄','🧅','🥔','🍠','🥐','🍞',
    '🥖','🧀','🥚','🍳','🥞','🧇','🥓','🍔','🍟','🍕','🌭','🥪','🌮','🌯','🥙','🍜',
    '🍝','🍣','🍱','🍤','🍙','🍚','🍛','🍲','🥧','🍦','🍰','🎂','🧁','🍩','🍪','🍫',
    '🍬','🍭','🍮','☕','🍵','🧃','🥤','🍶','🍺','🍻','🥂','🍷','🥃','🍸','🍹','🍾']},
  {id: 'activity', icon: '⚽', title: 'Activities & Objects', emojis: [
    '⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🎱','🏓','🏸','🥅','⛳','🏹','🎣','🥊',
    '🎽','🛹','🛷','⛸️','🥌','🎿','⛷️','🏂','🏋️','🤸','🤼','🤽','🤾','🤺','🏇','🧗',
    '🎮','🕹️','🎲','🧩','🎯','🎳','🎰','🎨','🎭','🎤','🎧','🎼','🎹','🥁','🎷','🎺',
    '🎸','🪕','🎻','📱','💻','⌨️','🖥️','🖨️','🖱️','💽','💾','📷','📸','🎥','📺','📻',
    '⏰','⌚','📡','🔋','🔌','💡','🔦','📖','📚','💰','💎','🔧','🔨','🧰','🔑','🔒',
    '🎉','🎊','🎈','🎁','🎀','🪅','🧨','🎇','🎆']},
  {id: 'travel', icon: '🚗', title: 'Travel & Places', emojis: [
    '🚗','🚕','🚙','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🚚','🚛','🚜','🛴','🚲','🛵',
    '🏍️','🚨','🚔','🚍','🚝','🚄','🚅','🚈','🚂','🚆','🚇','🚊','🚉','✈️','🛫','🛬',
    '🚀','🛸','🚁','⛵','🚤','🛥️','🛳️','⛴️','🚢','⚓','🚧','⛽','🗺️','🗿','🗽','🗼',
    '🏰','🏯','🏟️','🎡','🎢','🎠','⛲','⛱️','🏖️','🏝️','🏔️','⛰️','🌋','🏕️','⛺','🏠']},
  {id: 'flags', icon: '🏁', title: 'Flags', emojis: [
    '🏁','🚩','🎌','🏴','🏳️','🏳️‍🌈','🏴‍☠️','🇺🇸','🇬🇧','🇨🇦','🇦🇺','🇮🇳','🇩🇪','🇫🇷','🇮🇹','🇪🇸',
    '🇯🇵','🇰🇷','🇨🇳','🇧🇷','🇷🇺','🇲🇽','🇿🇦','🇳🇬','🇦🇪','🇸🇦','🇸🇬','🇮🇩','🇵🇰','🇧🇩','🇳🇿','🇮🇪']},
];

// Short aliases for what people actually type. Kept in sync with the iOS
// EmojiCatalog.aliases table.
const ALIASES = {
  '😂': ['lol', 'laugh', 'haha', 'funny', 'joy'],
  '🤣': ['lol', 'rofl', 'laugh', 'haha'],
  '😀': ['smile', 'happy'], '😃': ['smile', 'happy'], '😄': ['smile', 'happy'],
  '😁': ['smile', 'grin'], '😊': ['smile', 'happy', 'blush'], '🙂': ['smile'],
  '😍': ['love', 'heart eyes'], '🥰': ['love'], '😘': ['kiss', 'love'],
  '❤️': ['love', 'heart', 'red'], '💔': ['sad', 'breakup', 'heart'],
  '😢': ['cry', 'sad'], '😭': ['cry', 'sad', 'sob'], '😞': ['sad'],
  '👍': ['ok', 'yes', 'like', 'thumbs up', 'plus one', 'good'],
  '👎': ['no', 'dislike', 'thumbs down', 'bad'],
  '👌': ['ok', 'perfect'], '🙏': ['thanks', 'please', 'pray', 'namaste'],
  '🔥': ['fire', 'lit', 'hot'], '💯': ['100', 'perfect', 'hundred'],
  '🎉': ['party', 'celebrate', 'congrats', 'yay'],
  '🥳': ['party', 'celebrate', 'congrats'],
  '👏': ['clap', 'applause', 'bravo'],
  '😮': ['wow', 'surprised', 'shock'], '😲': ['wow', 'shock'],
  '🤔': ['think', 'hmm'], '😉': ['wink'], '😎': ['cool', 'sunglasses'],
  '✅': ['check', 'done', 'tick', 'yes'], '❌': ['cross', 'no', 'wrong'],
  '⚠️': ['warning', 'caution'], '🚀': ['rocket', 'ship', 'launch'],
  '💩': ['poop'], '🤖': ['bot', 'robot'], '👀': ['eyes', 'look'],
  '😴': ['sleep', 'tired'], '🥺': ['please', 'pleading', 'puppy'],
  '🤯': ['mind blown', 'shock'], '🙄': ['eyeroll', 'whatever'],
  '😡': ['angry', 'mad'], '🤬': ['angry', 'swear', 'curse'],
  '🍕': ['pizza', 'food'], '☕': ['coffee', 'tea'], '🍺': ['beer', 'drink'],
  '🎂': ['birthday', 'cake'], '🦖': ['dino', 'dinosaur', 'trex'],
  '🇮🇳': ['india', 'flag'], '🇺🇸': ['usa', 'america', 'flag'],
};

// Every catalog emoji, in category order, de-duplicated.
export const ALL_EMOJIS = (() => {
  const seen = new Set();
  const out = [];
  for (const c of EMOJI_CATEGORIES) {
    for (const e of c.emojis) {
      if (!seen.has(e)) {
        seen.add(e);
        out.push(e);
      }
    }
  }
  return out;
})();

// Searchable terms per emoji: category title + aliases. (The browser has no
// equivalent of iOS's Unicode-name transform, so the alias table plus the
// category name is the index.)
const SEARCH_INDEX = (() => {
  const idx = new Map();
  for (const c of EMOJI_CATEGORIES) {
    for (const e of c.emojis) {
      const terms = [c.title.toLowerCase()].concat(ALIASES[e] || []);
      idx.set(e, (idx.get(e) ? idx.get(e) + ' ' : '') + terms.join(' '));
    }
  }
  return idx;
})();

// Substring search over aliases + category names. Word-boundary hits rank
// first. Typing an emoji itself finds that emoji.
export function searchEmoji(rawQuery) {
  const q = (rawQuery || '').trim().toLowerCase();
  if (!q) {
    return [];
  }
  if (ALL_EMOJIS.indexOf(q) >= 0) {
    return [q];
  }
  const strong = [];
  const weak = [];
  for (const e of ALL_EMOJIS) {
    const terms = SEARCH_INDEX.get(e) || '';
    const at = terms.indexOf(q);
    if (at < 0) {
      continue;
    }
    (at == 0 || terms[at - 1] == ' ' ? strong : weak).push(e);
  }
  return strong.concat(weak);
}

// ---------------------------------------------------------------- recents

const RECENTS_KEY = 'emoji_recents';
export const MAX_RECENTS = 32;

function _store() {
  try {
    return (typeof localStorage != 'undefined') ? localStorage : null;
  } catch (e) {
    // Blocked by privacy settings.
    return null;
  }
}

// Most-recently-used emoji, newest first. Persisted across reloads.
export function recentEmojis() {
  const s = _store();
  if (!s) {
    return [];
  }
  try {
    const raw = JSON.parse(s.getItem(RECENTS_KEY));
    return Array.isArray(raw) ? raw.filter(e => typeof e == 'string' && e) : [];
  } catch (e) {
    return [];
  }
}

// Move `emoji` to the head of the recents list and persist. Returns the new list.
export function recordEmojiUse(emoji) {
  if (typeof emoji != 'string' || !emoji.trim()) {
    return recentEmojis();
  }
  const next = [emoji].concat(recentEmojis().filter(e => e != emoji)).slice(0, MAX_RECENTS);
  const s = _store();
  if (s) {
    try {
      s.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch (e) { /* quota / private mode: recents are best-effort */ }
  }
  return next;
}
